import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../config/runtime';
import { categoryLabel } from '../i18n/categoryNames';
import './ProjectsPage.css';

interface Requirement {
  id: number;
  title: string;
  description: string;
  budget_min?: number;
  budget_max?: number;
  deadline?: string;
  work_type: 'remote' | 'onsite' | 'hybrid';
  status: 'open' | 'in_progress' | 'closed';
  client_nickname: string;
  client_profile_image?: string;
  category_name: string;
  category_icon?: string;
  views_count: number;
  proposals_count: number;
  created_at: string;
}

interface Category {
  name: string;
  icon: string;
}

const ProjectsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [budgetFilter, setBudgetFilter] = useState('');
  const [workTypeFilter, setWorkTypeFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const navigate = useNavigate();
  const API_BASE = API_BASE_URL;

  const loadCategories = () => {
    setCategories([
      { name: '웹 개발', icon: '' },
      { name: '모바일 앱', icon: '' },
      { name: '디자인', icon: '' },
      { name: '마케팅', icon: '' },
      { name: '글쓰기', icon: '' },
      { name: '번역', icon: '' },
      { name: '영상 편집', icon: '' },
      { name: '기타', icon: '' },
    ]);
  };

  const loadRequirements = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${API_BASE}/projects?page=${currentPage}&limit=12&status=open`;

      if (searchInput) url += `&search=${encodeURIComponent(searchInput)}`;
      if (categoryFilter) url += `&category=${encodeURIComponent(categoryFilter)}`;
      if (budgetFilter) {
        const [min, max] = budgetFilter.split('-');
        url += `&min_budget=${min}&max_budget=${max}`;
      }
      if (workTypeFilter) url += `&work_type=${workTypeFilter}`;

      console.log('프로젝트 조회 URL:', url);

      const response = await fetch(url);
      const result = await response.json();

      if (result.success) {
        setRequirements(result.data);
        if (result.pagination) setTotalPages(result.pagination.totalPages);
      } else {
        throw new Error(result.message || '요구사항을 불러올 수 없습니다.');
      }
    } catch (error) {
      console.error('요구사항 로드 오류:', error);
      setRequirements([]);
    } finally {
      setLoading(false);
    }
  }, [API_BASE, currentPage, categoryFilter, budgetFilter, workTypeFilter, searchInput]);

  useEffect(() => {
    loadCategories();
    loadRequirements();
  }, [loadRequirements]);

  const handleSearch = () => {
    setCurrentPage(1);
    loadRequirements();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const viewRequirement = (requirementId: number) => {
    navigate(`/projects/${requirementId}`);
  };

  const formatBudgetRange = (min?: number, max?: number) => {
    if (!min && !max) return t('projects.negotiable');
    if (!min) return t('projects.budgetUpTo', { v: formatPrice(max!) });
    if (!max) return t('projects.budgetFrom', { v: formatPrice(min) });
    if (min === max) return t('projects.budgetExact', { v: formatPrice(min) });
    return t('projects.budgetRange', { min: formatPrice(min), max: formatPrice(max) });
  };

  const formatDeadline = (deadline?: string) => {
    if (!deadline) return t('projects.negotiable');
    
    const deadlineDate = new Date(deadline);
    const today = new Date();
    const diffTime = deadlineDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return t('projects.expired');
    if (diffDays === 0) return t('projects.dueToday');
    if (diffDays === 1) return t('projects.dueTomorrow');
    if (diffDays <= 7) return t('projects.daysLater', { n: diffDays });
    
    return deadlineDate.toLocaleDateString(i18n.language === 'ko' ? 'ko-KR' : 'en-US');
  };

  const getWorkTypeText = (workType: string) => {
    const types = {
      'remote': t('projects.workRemote'),
      'onsite': t('projects.workOnsite'),
      'hybrid': t('projects.workHybrid')
    };
    return types[workType as keyof typeof types] || t('projects.negotiable');
  };

  const getStatusText = (status: string) => {
    const statuses = {
      'open': t('projects.statusOpen'),
      'in_progress': t('projects.statusInProgress'),
      'closed': t('projects.statusClosed')
    };
    return statuses[status as keyof typeof statuses] || t('projects.statusUnknown');
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ko-KR').format(price);
  };

  return (
    <div className="projects-page">
      <main className="projects-main">
        <div className="container">
          <div className="page-header">
            <h1>{t('projects.title')}</h1>
            <p>{t('projects.subtitle')}</p>
          </div>

          <div className="search-section">
            <div className="search-form">
              <input
                type="text"
                className="search-input"
                placeholder={t('projects.searchPlaceholder')}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <select
                className="filter-select"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">{t('services.allCategories')}</option>
                {categories.map(category => (
                  <option key={category.name} value={category.name}>
                    {category.icon} {categoryLabel(category.name)}
                  </option>
                ))}
              </select>
              <select
                className="filter-select"
                value={budgetFilter}
                onChange={(e) => setBudgetFilter(e.target.value)}
              >
                <option value="">{t('projects.allBudgets')}</option>
                <option value="0-500000">{t('projects.budgetUnder500k')}</option>
                <option value="500000-1000000">{t('projects.budget500kTo1m')}</option>
                <option value="1000000-3000000">{t('projects.budget1mTo3m')}</option>
                <option value="3000000-999999999">{t('projects.budgetOver3m')}</option>
              </select>
              <select
                className="filter-select"
                value={workTypeFilter}
                onChange={(e) => setWorkTypeFilter(e.target.value)}
              >
                <option value="">{t('projects.allWorkTypes')}</option>
                <option value="remote">{t('projects.workRemote')}</option>
                <option value="onsite">{t('projects.workOnsite')}</option>
                <option value="hybrid">{t('projects.workHybrid')}</option>
              </select>
              <button className="btn btn-primary" onClick={handleSearch}>{t('common.search')}</button>
            </div>
          </div>

          <div className="projects-container">
            {loading ? (
              <div className="loading">{t('projects.loading')}</div>
            ) : requirements.length === 0 ? (
              <div className="empty-state">
                <h3>{t('services.noResults')}</h3>
                <p>{t('services.noResultsDesc')}</p>
              </div>
            ) : (
              <div className="projects-grid">
                {requirements.map(requirement => {
                  const clientInitial = requirement.client_nickname ? 
                    requirement.client_nickname.charAt(0).toUpperCase() : 'C';
                  const budget = formatBudgetRange(requirement.budget_min, requirement.budget_max);
                  const deadline = formatDeadline(requirement.deadline);
                  const workTypeClass = `work-type-${requirement.work_type}`;
                  const workTypeText = getWorkTypeText(requirement.work_type);
                  const statusText = getStatusText(requirement.status);

                  return (
                    <div 
                      key={requirement.id} 
                      className="project-card" 
                      onClick={() => viewRequirement(requirement.id)}
                    >
                      <div className="project-header">
                        <div className="project-category">
                          {categoryLabel(requirement.category_name) || t('projects.etc')}
                        </div>
                        <div className="project-status status-open">{statusText}</div>
                      </div>
                      <div className="project-title">{requirement.title}</div>
                      <div className="project-client">
                        <div className="client-avatar">{clientInitial}</div>
                        <span>{requirement.client_nickname || t('projects.client')}</span>
                      </div>
                      <div className="project-description">{requirement.description}</div>
                      <div className="project-meta">
                        <div className="meta-item">
                          <div className="meta-label">{t('projects.budget')}</div>
                          <div className="meta-value budget-range">{budget}</div>
                        </div>
                        <div className="meta-item">
                          <div className="meta-label">{t('projects.deadline')}</div>
                          <div className="meta-value">{deadline}</div>
                        </div>
                      </div>
                      <div className="project-footer">
                        <div className="project-stats">
                          <span>{t('projects.views')} {requirement.views_count || 0}</span>
                          <span>{t('projects.proposals')} {requirement.proposals_count || 0}</span>
                        </div>
                        <div className={`work-type-badge ${workTypeClass}`}>{workTypeText}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                {t('common.prev')}
              </button>
              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                const page = i + 1;
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={currentPage === page ? 'current' : ''}
                  >
                    {page}
                  </button>
                );
              })}
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ProjectsPage;
